import { useState, useEffect, useCallback, useRef } from 'react';
import { collection, getDocs, query, where, limit, orderBy } from 'firebase/firestore';
import { ref, getDownloadURL, listAll } from 'firebase/storage';
import { db, storage } from '../../firebase';
import { Event } from '../../types/Event';

export const useEventPhotos = (events: Event[]) => {
  const [eventPhotos, setEventPhotos] = useState<Record<string, string[]>>(() => {
    const savedPhotos = localStorage.getItem('eventPhotos_v1');
    return savedPhotos ? JSON.parse(savedPhotos) : {};
  });

  const fetchedRef = useRef<Record<string, boolean>>({});

  useEffect(() => {
    if (Object.keys(eventPhotos).length > 0) {
      localStorage.setItem('eventPhotos_v1', JSON.stringify(eventPhotos));
    }
  }, [eventPhotos]);

  const fetchPhotosFromStorage = async (startDate: Date, endDate: Date): Promise<string[]> => {
    try {
      const startYear = startDate.getFullYear();
      const startMonth = startDate.getMonth() + 1;
      const endYear = endDate.getFullYear();
      const endMonth = endDate.getMonth() + 1;
      
      const foldersToCheck: string[] = [];
      
      if (startYear === endYear && startMonth === endMonth) {
        foldersToCheck.push(`photos/${startYear}/${startMonth.toString().padStart(2, '0')}`);
      } else {
        let currentYear = startYear;
        let currentMonth = startMonth;
        
        while (currentYear < endYear || (currentYear === endYear && currentMonth <= endMonth)) {
          foldersToCheck.push(`photos/${currentYear}/${currentMonth.toString().padStart(2, '0')}`);
          
          currentMonth++;
          if (currentMonth > 12) {
            currentMonth = 1;
            currentYear++;
          }
        }
      }
      
      const allPhotos: string[] = [];
      
      for (const folder of foldersToCheck) {
        try {
          const folderRef = ref(storage, folder);
          const filesList = await listAll(folderRef);
          
          for (const fileRef of filesList.items) {
            const dateMatch = fileRef.name.match(/(\d{8})_(\d{6})/);
            
            if (dateMatch) {
              const dateStr = dateMatch[1];
              const timeStr = dateMatch[2];
              const year = parseInt(dateStr.substring(0, 4));
              const month = parseInt(dateStr.substring(4, 6)) - 1;
              const day = parseInt(dateStr.substring(6, 8));
              const hour = parseInt(timeStr.substring(0, 2));
              const minute = parseInt(timeStr.substring(2, 4));
              const second = parseInt(timeStr.substring(4, 6));
              
              const photoDate = new Date(year, month, day, hour, minute, second);
              
              if (photoDate >= startDate && photoDate <= endDate) {
                allPhotos.push(fileRef.fullPath);
              }
            } else {
              allPhotos.push(fileRef.fullPath);
            }
          }
        } catch (err) {
          console.error(`Erreur lors de la liste des fichiers dans ${folder}:`, err);
        }
      }
      
      // Mélanger et prendre 4 photos
      const shuffled = allPhotos.sort(() => 0.5 - Math.random()).slice(0, 4);
      const urls = await Promise.all(shuffled.map(path => getDownloadURL(ref(storage, path))));
      return urls;
      
    } catch (err) {
      console.error('Erreur lors de la récupération des photos depuis Storage:', err);
      return [];
    }
  };

  const fetchEventPhotos = useCallback(async (event: Event) => {
    if (!event.id || !event.startDate || !event.endDate) return;

    try {
      const eventStart = new Date(event.startDate);
      const eventEnd = new Date(event.endDate);
      
      const startDate = new Date(eventStart);
      startDate.setDate(startDate.getDate() - 2);
      
      const endDate = new Date(eventEnd);
      endDate.setDate(endDate.getDate() + 2);
      
      const photosRef = collection(db, 'photos');
      const q = query(
        photosRef,
        where('date', '>=', startDate),
        where('date', '<=', endDate),
        orderBy('date', 'desc'),
        limit(20)
      );
      
      const querySnapshot = await getDocs(q);
      let foundUrls: string[] = [];

      // 1. Essayer par date
      if (!querySnapshot.empty) {
        const photoPromises = querySnapshot.docs.map(async (doc) => {
          const data = doc.data();
          if (data.url) return data.url;
          if (data.storagePath) {
            try {
              return await getDownloadURL(ref(storage, data.storagePath));
            } catch (e) {
              return null;
            }
          }
          return null;
        });

        const results = await Promise.all(photoPromises);
        const validUrls = results.filter((url): url is string => url !== null);
        foundUrls = [...foundUrls, ...validUrls];
      }

      // 2. Si pas assez de photos, essayer par path
      if (foundUrls.length < 4) {
        const eventYear = eventStart.getFullYear();
        const eventMonth = eventStart.getMonth();
        const monthStr = (eventMonth + 1).toString().padStart(2, '0');
        const pathPrefix = `photos/${eventYear}/${monthStr}/`;

        const pathQuery = query(
            photosRef,
            where('path', '>=', pathPrefix),
            where('path', '<', pathPrefix + '\uf8ff'),
            limit(20)
        );

        const pathSnapshot = await getDocs(pathQuery);
        if (!pathSnapshot.empty) {
             const photoPromises = pathSnapshot.docs.map(async (doc) => {
                const data = doc.data();
                if (data.url) return data.url;
                const path = data.storagePath || data.path;
                if (path) {
                    try {
                        return await getDownloadURL(ref(storage, path));
                    } catch (e) {
                        return null;
                    }
                }
                return null;
            });
            const results = await Promise.all(photoPromises);
            const validUrls = results.filter((url): url is string => url !== null);
            
            // Ajouter seulement les nouvelles URLs
            const newUrls = validUrls.filter(url => !foundUrls.includes(url));
            foundUrls = [...foundUrls, ...newUrls];
        }
      }

      // 3. Si toujours pas assez de photos, fallback final sur Storage direct
      if (foundUrls.length < 4) {
        const storageUrls = await fetchPhotosFromStorage(startDate, endDate);
        const newUrls = storageUrls.filter(url => !foundUrls.includes(url));
        foundUrls = [...foundUrls, ...newUrls];
      }

      // Mélanger et prendre 4 photos
      const finalPhotos = foundUrls.sort(() => 0.5 - Math.random()).slice(0, 4);
      
      setEventPhotos(prev => ({ ...prev, [event.id as string]: finalPhotos }));

    } catch (err) {
      console.error('Erreur globale fetchEventPhotos', err);
      setEventPhotos(prev => ({ ...prev, [event.id as string]: [] }));
    }
  }, []);

  useEffect(() => {
    const now = new Date();
    const pastEvents = events.filter(event => {
      const endDate = new Date(event.endDate);
      return endDate <= now;
    });
    
    pastEvents.forEach(event => {
      if (event.id && !fetchedRef.current[event.id]) {
        const hasPhotos = eventPhotos[event.id] && eventPhotos[event.id].length > 0;
        // On cherche si pas encore en cache, OU si le cache est vide mais l'événement est très récent (moins de 3 jours)
        if (!eventPhotos[event.id] || (!hasPhotos && (now.getTime() - new Date(event.endDate).getTime() < 3 * 24 * 60 * 60 * 1000))) {
          fetchedRef.current[event.id] = true;
          fetchEventPhotos(event);
        }
      }
    });
  }, [events, eventPhotos, fetchEventPhotos]);

  return eventPhotos;
};
